import { memo } from 'react';
import { useReduxState } from 'hooks';
import { Icon } from 'components/common/icons';
import { FileUploader } from 'components/common/uploader';
import { editorSocket } from 'components/editor/socket';

import type { EditorHeaderStateProps } from 'components/editor/header/EditorHeader';

type Props = Pick<
  EditorHeaderStateProps,
  'currentChapter' | 'importVisible' | 'isImporting'
>;

const EditorImport = ({
  currentChapter,
  importVisible,
  isImporting
}: Props) => {
  const { messages, theme } = useReduxState();

  const handleUploadSuccess = ({ filePath }: { filePath: string }) =>
    editorSocket.importDocument({
      chapterId: currentChapter.id,
      filePath
    });

  return !importVisible ? null : (
    <div className="import">
      <div className="title">
        <div className="title-content" data-tooltip={messages.editorImportInfo}>
          {messages.editorImportTitle}
          <Icon
            icon="question-circle"
            style={{ fontSize: '1.4rem', marginLeft: 5 }}
          />
        </div>
      </div>

      <FileUploader
        allowedFileTypes={[
          'application/msword',
          'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
          'application/vnd.oasis.opendocument.text',
          'application/rtf',
          'text/rtf',
          'application/vnd.ms-powerpoint',
          'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          'application/vnd.oasis.opendocument.presentation',
          'application/pdf',
          'application/x-tex',
          '.doc',
          '.docx',
          '.odt',
          '.rtf',
          '.ppt',
          '.pptx',
          '.odp',
          '.pdf',
          '.tex',
          '.zip'
        ]}
        destination="local"
        dragDrop={true}
        fileTypeErrorText={
          <div>
            <div>
              {`${messages.uploaderFileTypeError}.`}
              {` ${messages.uploaderAllowedFileTypes}:`}
            </div>
            <div>
              {`.doc, .docx, .odt, .rtf, .ppt, .pptx, .odp, .pdf, .tex`}
            </div>
          </div>
        }
        icon="file"
        id={`import-uploader`}
        inProgress={isImporting}
        inProgressText={messages.editorImportInProgress}
        keyPrefix="uploads/files/"
        onUploadSuccess={handleUploadSuccess}
        placeholder={
          <div>
            <div>{messages.uploaderChooseFileOrDrag}</div>
            <div>{`(${messages.editorImportFiles})`}</div>
          </div>
        }
        uploadActionText={messages.commonImport}
      />

      <style jsx>{`
        .import {
          background-color: ${theme.lightGrey};
          border-radius: ${theme.cardBorderRadius};
          margin: 15px 0 5px;
        }
        .title {
          background-color: ${theme.lightGreyDark};
          border-radius: ${`${theme.cardBorderRadius} ${theme.cardBorderRadius} 0 0`};
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 700;
          padding: 10px 15px;
        }
        .title-content {
          display: inline-block;
        }
      `}</style>
    </div>
  );
};

export default memo(EditorImport);
